import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    overheadRateOutsideFaculty: { type: Number, default: 11 },
    overheadRateOnFaculty: { type: Number, default: 22 }
  }

  connect() {
    this.recalculate()
  }

  recalculate() {
    const expectedStudentCount = this.numberFromInput("expected_student_count")
    const directWageCosts = this.numberFromInput("direct_wage_costs")
    const directMaterialCosts = this.numberFromInput("direct_material_costs")
    const externalServices = this.numberFromInput("other_services_purchases_total")
    const otherIndirectCosts = this.numberFromInput("dpp_costs")
    const vatRate = this.numberFromInput("vat_rate") ?? 21
    const facultyOverhead = this.facultyOverhead()

    const totalCostsWithoutOverhead = this.sum(
      this.multiply(directWageCosts, 1.348),
      directMaterialCosts,
      externalServices,
      otherIndirectCosts
    )
    const overheadCosts11 = this.multiply(totalCostsWithoutOverhead, this.overheadRateOutsideFacultyValue / 100)
    const overheadCosts22 = this.multiply(totalCostsWithoutOverhead, this.overheadRateOnFacultyValue / 100)
    const selectedOverheadCosts = facultyOverhead ? overheadCosts22 : overheadCosts11
    const costPriceWithoutProfit = this.sum(totalCostsWithoutOverhead, selectedOverheadCosts)
    const calculatedProfit = this.multiply(costPriceWithoutProfit, 0.05)
    const priceWithoutVat = this.sum(costPriceWithoutProfit, calculatedProfit)
    const vatAmount = this.multiply(priceWithoutVat, vatRate / 100)
    const recommendedPrice = this.sum(priceWithoutVat, vatAmount)
    const preferredPrice = this.divide(recommendedPrice, expectedStudentCount)

    this.writeOutput("recommended_price", recommendedPrice)
    this.writeOutput("vat_amount", vatAmount)
    this.writeOutput("price_without_vat", priceWithoutVat)
    this.writeOutput("calculated_profit", calculatedProfit)
    this.writeOutput("cost_price_without_profit", costPriceWithoutProfit)
    this.writeOutput("overhead_costs_11", overheadCosts11)
    this.writeOutput("overhead_costs_22", overheadCosts22)
    this.writeOutput("total_costs_without_overhead", totalCostsWithoutOverhead)
    this.writeOutput("preferred_price", preferredPrice)
    this.syncOverheadVisibility(facultyOverhead)
  }

  numberFromInput(key) {
    const element = this.element.querySelector(`[data-calculation-input="${key}"]`)
    if (!element) return null

    const normalizedValue = element.value.trim().replace(",", ".")
    if (normalizedValue.length === 0) return null

    const value = Number(normalizedValue)
    return Number.isFinite(value) ? value : null
  }

  heldAtValue() {
    const element = this.element.querySelector(`[data-calculation-input="held_at"]`)
    return element?.value || ""
  }

  facultyOverhead() {
    return ["on_faculty", "vv_stredisko_straz", "vv_stredisko_mulda", "Na fakultě", "VV středisko Stráž", "VV středisko Mulda"].includes(this.heldAtValue())
  }

  writeOutput(key, value) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    if (elements.length === 0) return

    const rawValue = value === null ? "" : this.formatNumber(value)
    elements.forEach((element) => {
      if ("value" in element) {
        element.value = rawValue
        if (key === "preferred_price") element.dispatchEvent(new Event("input", { bubbles: true }))
      } else {
        element.textContent = this.formatDisplayValue(value, element.dataset.calculationOutputFormat)
      }
    })
  }

  syncOverheadVisibility(onFaculty) {
    this.setOutputHidden("overhead_costs_11", onFaculty)
    this.setOutputHidden("overhead_costs_22", !onFaculty)
  }

  setOutputHidden(key, hidden) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    elements.forEach((element) => {
      const card = element.closest("[data-calculation-output-card]")
      if (card) card.hidden = hidden
    })
  }

  sum(...values) {
    if (values.every((value) => value === null)) return null

    return values.reduce((total, value) => total + (value ?? 0), 0)
  }

  multiply(...values) {
    if (values.some((value) => value === null)) return null

    return values.reduce((total, value) => total * value, 1)
  }

  divide(first, second) {
    if (first === null || second === null || second === 0) return null

    return first / second
  }

  formatNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: false,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }

  formatDisplayValue(value, format) {
    if (value === null) return "—"
    if (format === "currency") return `${this.formatGroupedNumber(value)} Kč`

    return this.formatGroupedNumber(value)
  }

  formatGroupedNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: true,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }
}
